import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import * as CPF from 'cpf'
import { Colors } from '../../styles'
import {
  validThruValidator,
  cardNumberValidator,
  cvvValidator,
} from './validators'
import { cpfMask, cardNumberMask, validThruMask, cvvMask } from './masks'
import { CreditCardForm as CreditCardFormType } from './types'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
  withStyles,
} from '@material-ui/core'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import ReCAPTCHA from 'react-google-recaptcha'

const Wrapper = styled.div`
  flex: 1;
  justify-content: space-between;
`

const InputWrapper = styled.div<{ fullWidth?: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '30%')};
  margin-bottom: 30px;
  font-family: Inter;
`
const ValueInput = styled(TextField)<TextFieldProps>`
  width: 100%;

  .MuiOutlinedInput-root {
    line-height: 30px;
    font-size: 18px;
    font-weight: 700;
    padding: 0 15px;
    border: 1px solid ${Colors.GREY[200]};
    color: ${Colors.GREY[200]};
    font-family: Inter;
    outline: none;
    box-shadow: none;
    border-radius: 8px;
    &.Mui-focused {
      color: ${Colors.GREY[400]};
    }
  }
  .MuiInputLabel-root {
    color: ${Colors.GREY[200]};
    font-family: Inter;
    font-weight: bold;
    &.Mui-focused {
      font-size: 18px;
    }
  }
  .Mui-focused {
    border-top-color: transparent;
    fieldset {
      border-color: ${Colors.GREY[200]} !important;
    }
  }
`

const PurpleRadio = withStyles({
  root: {
    color: Colors.PURPLE[50],
    '&$checked': {
      color: Colors.PURPLE[400],
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />)

type Props = {
  onSave: (creditCard: CreditCardFormType, recaptchaToken: string) => void
}

const CardWithDocumentForm = ({ onSave }: Props): JSX.Element => {
  const [form, setForm] = useState<CreditCardFormType>({
    cardNumber: '',
    cardName: '',
    cardDueDate: '',
    cardCVV: '',
    document: '',
    documentType: 'cpf',
  })
  const [touched, setTouched] = useState({
    cardNumber: false,
    cardName: false,
    cardDueDate: false,
    cardCVV: false,
    document: false,
    documentType: true,
  })

  const [recaptchaToken, setRecaptchaToken] = useState<string>()

  const cardNumberRef = useRef(null)
  const cardNameRef = useRef(null)
  const cardDueDateRef = useRef(null)
  const cardCVVRef = useRef(null)
  const documentRef = useRef(null)

  const onTypeFormField = (
    text: string,
    id: string,
    touched: boolean = true,
  ): void => {
    setForm((prevForm) => ({
      ...prevForm,
      [id]: text,
    }))
    setTouched((prevTouched) => ({
      ...prevTouched,
      [id]: touched,
    }))
  }

  const checkIsFormValid = (): boolean => {
    const isDocumentValid =
      form.documentType === 'cpf' ? CPF.isValid(form.document) : true
    const isCardNumberValid = cardNumberValidator(form.cardNumber)
    const isDueDateValid = validThruValidator(form.cardDueDate).isValid
    const isCVVValid = cvvValidator(form.cardCVV)

    return (
      isDocumentValid &&
      isCardNumberValid &&
      isDueDateValid &&
      isCVVValid &&
      !!form.cardNumber &&
      !!form.cardName &&
      !!form.cardDueDate &&
      !!form.cardCVV &&
      !!form.document
    )
  }

  return (
    <Wrapper>
      <InputWrapper fullWidth>
        <ValueInput
          label="Número"
          error={touched.cardNumber && !cardNumberValidator(form.cardNumber)}
          helperText={
            touched.cardNumber &&
            !cardNumberValidator(form.cardNumber) &&
            'Inválido'
          }
          variant="outlined"
          type="text"
          name="card-number"
          autoComplete="card number"
          value={form.cardNumber}
          onChange={(event) =>
            onTypeFormField(
              cardNumberMask(event.currentTarget.value),
              'cardNumber',
            )
          }
          ref={cardNumberRef}
          onKeyDown={(event) => {
            if (event.keyCode === 13) {
              cardDueDateRef &&
                cardDueDateRef.current &&
                cardDueDateRef.current.focus()
              event.preventDefault()
            }
          }}
          inputMode="decimal"
        />
      </InputWrapper>
      <InputWrapper fullWidth>
        <ValueInput
          label="Nome no cartão"
          error={touched.cardName && !form.cardName}
          helperText={touched.cardName && !form.cardName && 'Inválido'}
          variant="outlined"
          type="text"
          name="card-name"
          autoComplete="card name"
          value={form.cardName}
          onChange={(event) =>
            onTypeFormField(event.currentTarget.value, 'cardName')
          }
          ref={cardNameRef}
          onKeyDown={(event) => {
            if (event.keyCode === 13) {
              documentRef && documentRef.current && documentRef.current.focus()
              event.preventDefault()
            }
          }}
          inputMode="text"
        />
      </InputWrapper>
      <InputWrapper fullWidth>
        <ValueInput
          label="Validade"
          error={touched.cardDueDate && !validThruValidator(form.cardDueDate)}
          helperText={
            touched.cardDueDate &&
            !validThruValidator(form.cardDueDate) &&
            'Inválido'
          }
          variant="outlined"
          type="text"
          name="card-due-date"
          value={form.cardDueDate}
          onChange={(event) =>
            onTypeFormField(
              validThruMask(event.currentTarget.value),
              'cardDueDate',
            )
          }
          ref={cardDueDateRef}
          onKeyDown={(event) => {
            if (event.keyCode === 13) {
              cardCVVRef && cardCVVRef.current && cardCVVRef.current.focus()
              event.preventDefault()
            }
          }}
          inputMode="text"
        />
      </InputWrapper>
      <InputWrapper fullWidth>
        <ValueInput
          label="CVV"
          error={touched.cardCVV && !cvvValidator(form.cardCVV)}
          helperText={
            touched.cardCVV && !cvvValidator(form.cardCVV) && 'Inválido'
          }
          variant="outlined"
          type="text"
          name="card-CVV"
          value={form.cardCVV}
          onChange={(event) =>
            onTypeFormField(cvvMask(event.currentTarget.value), 'cardCVV')
          }
          ref={cardCVVRef}
          onKeyDown={(event) => {
            if (event.keyCode === 13) {
              cardNameRef && cardNameRef.current && cardNameRef.current.focus()
              event.preventDefault()
            }
          }}
          inputMode="text"
        />
      </InputWrapper>
      <InputWrapper fullWidth>
        <RadioGroup
          row
          value={form.documentType}
          onChange={(event) => {
            onTypeFormField('', 'document', false)
            onTypeFormField(event.currentTarget.value, 'documentType')
          }}
        >
          <FormControlLabel value="cpf" control={<PurpleRadio />} label="CPF" />
          <FormControlLabel
            value="passport"
            control={<PurpleRadio />}
            label="Passaporte"
          />
        </RadioGroup>

        {form.documentType === 'cpf' ? (
          <ValueInput
            label="CPF"
            error={touched.document && !CPF.isValid(form.document)}
            helperText={
              touched.document && !CPF.isValid(form.document) && 'Inválido'
            }
            variant="outlined"
            value={form.document}
            onChange={(event) =>
              onTypeFormField(cpfMask(event.currentTarget.value), 'document')
            }
            ref={documentRef}
            onKeyDown={(event) => {
              if (event.keyCode === 13) {
                checkIsFormValid() && onSave({ ...form }, recaptchaToken)
                event.preventDefault()
              }
            }}
            inputMode="decimal"
          />
        ) : (
          <ValueInput
            label="Passaporte"
            error={touched.document && !form.document}
            helperText={touched.document && !form.document && 'Inválido'}
            variant="outlined"
            type="text"
            value={form.document}
            onChange={(event) =>
              onTypeFormField(event.currentTarget.value, 'document')
            }
            ref={documentRef}
            onKeyDown={(event) => {
              if (event.keyCode === 13) {
                documentRef &&
                  documentRef.current &&
                  documentRef.current.focus()
                event.preventDefault()
              }
            }}
            inputMode="text"
          />
        )}
      </InputWrapper>
      <ReCAPTCHA
        sitekey="6LcGCnopAAAAAE1f7lLmBNhpYJqxY3YxKcLM9P5e"
        onChange={(value) => setRecaptchaToken(value)}
      />
      <ButtonShadow
        disabled={!checkIsFormValid()}
        onPress={() => onSave({ ...form }, recaptchaToken)}
        label="Avançar"
      />
    </Wrapper>
  )
}

export default CardWithDocumentForm
